import React, { useState, useMemo } from "react";
import "./MarketplaceCards.scss";
import moment from "moment";
import { CTA, LabelledCheckbox } from "@coverme/forms";
import { CircularProgressbar, buildStyles } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";
import { WarningIcon } from "../Icons/WarningIcon";
import { NoteItem } from "../Notes/MiniFacesheetNotes";

function toMoney(income) {
    let defaultIncome = 0;
    if (income == null) {
        return defaultIncome.toFixed(2);
    }

    return income.toFixed(2);
}

function getChartColors(percentage) {
    if (percentage > 71) {
        return "green";
    } else if (percentage >= 41) {
        return "#ffcc66";
    } else {
        return "red";
    }
}

function getAssetsFromLexis(ssn) {
    return [];
}

function getRisks({ ssn }) {
    if (ssn === "666-57-2368") {
        return ["Excessive assets", "Excessive income - verification critical", "Trust < 5 years old"];
    } else if (ssn === "666-57-7521") {
        return ["Charity is risky", "Referral source", "Individual available to assist lives far from facility."];
    }
    return ["Charity is risky", "Referral source", "Individual available to assist lives far from facility."];
}

function getVerifications(verification) {
    const data = verification.data;
    const identityVerificationResult =
        data["nameVerification"]?.result === "Verified" &&
        data["ssnVerification"]?.result === "Verified" &&
        data["dateOfBirthVerification"]?.result === "Verified";

    const residencyVerification = data["addressVerification"]?.result === "Verified";

    return {
        identity: identityVerificationResult,
        residency: residencyVerification,
        citizenship: identityVerificationResult && residencyVerification,
    };
}

export const LTCCard = ({ patient, households, notes, verifications, onApprove, onDeny, program, caseClues }) => {

    let comprehensiveCaseClues = [...program.score.reasons, ...caseClues];

    const magiHousehold = useMemo(() => {
        return households.filter(x => x.householdType?.name === "Interview")[0];
    }, [households]);

    const verificationResults = useMemo(() => {
        return getVerifications(verifications);
    }, [verifications]);
    const percentage = useMemo(() => {
        return program?.score?.value;
    }, [program]);
    let progressStatus = getChartColors(percentage);
    const risks = program.score.reasons;

    return (
        <div className="marketplace-cards__long-term-care-card">
            <h2>MEDICAID ELIGIBILITY ASSESSMENT</h2>
            <div className="marketplace-cards__long-term-care-card__top-level">
                <div className="row-title">
                    <h3>Confidence Score</h3>
                </div>
                <div className="progress-bar">
                    <CircularProgressbar
                        value={percentage}
                        text={`${percentage}`}
                        styles={buildStyles({
                            backgroundColor: "#000",
                            textColor: "black",
                            pathColor: progressStatus,
                            trailColor: "transparent",
                        })}
                    />
                </div>
                <div className="ltc-approve-buttons">
                    <CTA size={"medium"} ctaType={"button"} styleType={"blue"} click={onApprove}>
                        Approve
                    </CTA>
                    <CTA size={"medium"} ctaType={"button"} styleType={"red"} click={onDeny}>
                        Deny
                    </CTA>
                </div>
            </div>
            <hr />
            <div className="marketplace-cards__long-term-care-card__identity-verification">
                <div className="row-title">
                    <h3>Critical Factors</h3>
                </div>

                <div>
                    <LabelledCheckbox
                        labelText={`Identity`}
                        id={`Identity`}
                        value={`Identity`}
                        checked={verificationResults.identity}
                        groupName="identity"
                        changeEvent={() => {}}
                    />
                </div>

                <div>
                    <LabelledCheckbox
                        labelText={`Residency`}
                        id={`Residency`}
                        value={`Residency`}
                        checked={verificationResults.residency}
                        groupName="identity"
                        changeEvent={() => {}}
                    />
                </div>
                <div>
                    <LabelledCheckbox
                        labelText={`Citizenship`}
                        id={`Citizenship`}
                        value={`Citizenship`}
                        checked={verificationResults.citizenship}
                        groupName="identity"
                        changeEvent={() => {}}
                    />
                </div>
            </div>
            <hr />
            <div className="marketplace-cards__long-term-care-card__program-risks">
                <div className="section-title">
                    <h3>Identified Program Risks</h3>
                </div>
                <div className="risk-items">
                    <ul>
                        {comprehensiveCaseClues.map((element, index) => {
                            return (
                                <li key={`risks_${index}`}>
                                    <p>
                                        <WarningIcon />
                                        {element}
                                    </p>
                                </li>
                            );
                        })}
                        {comprehensiveCaseClues.length === 0 && (
                            <div>
                                <p>No Risks Identified</p>
                            </div>
                        )}
                    </ul>
                </div>
            </div>
            <hr />
            <div className="marketplace-cards__long-term-care-card__financials">
                <div className="section-title">
                    <h3>Assets</h3>
                </div>
                <div className="total">
                    <label className="left-content">
                        <h6>Total Asset Value:</h6>
                        <p>${toMoney(magiHousehold?.financials?.totalAssetValue)}</p>
                    </label>

                    <label className="right-content">
                        <h6>Total Discovered Value:</h6>
                        <p>
                            {program.totalDiscoveredAssetValue > 0
                                ? "$" + toMoney(program.totalDiscoveredAssetValue)
                                : "NA"}
                        </p>
                    </label>
                </div>
                <div className="list">
                    {magiHousehold?.financials?.assets?.length > 0 && (
                        <table>
                            <thead>
                                <tr>
                                    <th>Date Added</th>
                                    <th>Type</th>
                                    <th>Assets</th>
                                </tr>
                            </thead>
                            <tbody>
                                {magiHousehold?.financials?.assets?.map(function AssetItem(x, i) {
                                    return (
                                        <tr key={`asset_${i}`}>
                                            <td>{moment().format("MM/DD/YYYY")}</td>
                                            <td>{x.assetType.name}</td>
                                            <td>${toMoney(x.value)}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
            <hr />
            <div className="marketplace-cards__long-term-care-card__financials">
                <div className="section-title">
                    <h3>Monthly Income</h3>
                </div>

                <div className="total">
                    <h6>Total Monthly Income:</h6>
                    <p>${toMoney(magiHousehold?.financials?.totalMonthlyIncome)}</p>
                </div>
                {magiHousehold?.financials?.incomes?.length > 0 && (
                    <div className="list">
                        <table>
                            <thead>
                                <tr>
                                    <th>Date Added</th>
                                    <th>Type</th>
                                    <th>Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                {magiHousehold?.financials?.incomes?.map((x, i) => {
                                    return (
                                        <tr key={`income_${i}`}>
                                            <td>{moment().format("MM/DD/YYYY")}</td>
                                            <td>{x.incomeType.name}</td>
                                            <td>${toMoney(x.monthlyAmount)}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <hr />
            <div className="marketplace-cards__long-term-care-card__notes">
                <div className="section-title">
                    <h3>Notes</h3>
                </div>
                {notes != null && (
                    <div>
                        {notes.map((x, i) => (
                            <NoteItem {...x} key={`notes_${i}`} />
                        ))}
                        {notes.length === 0 && <p className="no-notes-found">No notes found</p>}
                    </div>
                )}
            </div>
        </div>
    );
};
